import React, { useEffect } from 'react';
import { css, Global, useTheme } from '@emotion/react';
import styled from 'types/theme.types';
import { Menu } from 'antd';
import ScrollIntoViewIfNeeded from 'react-scroll-into-view-if-needed';

import type { MenuProps as AntdMenuProps } from 'antd/lib/menu';
import { legacyOnly, newDesignOnly, type Env0Theme } from 'constants/themes.constants';
import { typeStyle } from 'constants/typography';

import { useNavigate, useLocation } from 'react-router-dom';

type BaseMenuProps = {
  setActiveMenu: React.Dispatch<string>;
  activeMenu: string;
  menus: readonly string[];
  children: React.ReactNode;
  renderMenuItem?: (key: string) => React.ReactNode;
};

/**
 * `compact` is the former menu2: a shorter bar, no url sync, no scroll anchor, no custom actions.
 * It exists so the switch-off render of its three call sites stays pixel-identical; the new design
 * collapses both variants onto one treatment, and APO-484 deletes the prop.
 *
 * A union rather than one optional bag: the compact branch renders no `customActions`, so accepting
 * the prop there would swallow it silently — `never` turns that into a compile error.
 */
export type MenuProps = BaseMenuProps &
  ({ variant?: 'page'; customActions?: React.ReactNode } | { variant: 'compact'; customActions?: never });

const OVERFLOW_POPUP_CLASS = 'env0-tabs-menu-overflow';

const overflowPopupStyles = css`
  .${OVERFLOW_POPUP_CLASS} .ant-menu-item {
    ${typeStyle.labelMd}
    color: var(--text-body);
  }

  .${OVERFLOW_POPUP_CLASS} .ant-menu-item::first-letter {
    text-transform: uppercase;
  }

  .${OVERFLOW_POPUP_CLASS} .ant-menu-item-selected {
    color: var(--text-heading);
  }
`;

export const TAB_SEARCH_PARAM_NAME = 'tabname';
export const TAB_ANCHOR_HASH = '#tab';

const TabsMenu: React.FunctionComponent<MenuProps> = ({
  activeMenu,
  setActiveMenu,
  menus,
  children,
  renderMenuItem,
  customActions,
  variant = 'page'
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const syncsWithUrl = variant === 'page';

  useEffect(() => {
    if (!syncsWithUrl) return;

    const searchParams = new URLSearchParams(location.search);

    if (searchParams.has(TAB_SEARCH_PARAM_NAME)) {
      const tab = searchParams.get(TAB_SEARCH_PARAM_NAME);

      if (tab && menus.includes(tab)) {
        setActiveMenu(tab);
      }
    }
  }, [location, setActiveMenu, menus, syncsWithUrl]);

  const onMenuClick: AntdMenuProps['onClick'] = event => {
    const key = event.key.toString();

    if (syncsWithUrl) {
      const searchParams = new URLSearchParams(location.search);

      searchParams.set(TAB_SEARCH_PARAM_NAME, key);
      location.search = searchParams.toString();
      navigate(location);
    }

    setActiveMenu(key);
  };

  // The legacy style is applied inline because the Menu adjusts the items inside a dropdown in Tablet
  // and smaller viewports and the Styled CSS is not applied to those items.
  const renderMenuItemFunc =
    renderMenuItem ??
    (theme.isNewDesign
      ? (menu: string) => menu
      : (menu: string) => <div style={{ textTransform: 'uppercase' }}>{menu}</div>);

  const menuProps = {
    onClick: onMenuClick,
    selectedKeys: [activeMenu],
    mode: 'horizontal' as const,
    ...(theme.isNewDesign ? { overflowedIndicatorPopupClassName: OVERFLOW_POPUP_CLASS } : {}),
    items: menus.map(menuKey => ({
      key: menuKey,
      label: renderMenuItemFunc(menuKey),
      'data-e2e': `menu-${menuKey}`
    }))
  };

  return (
    <Container data-e2e={syncsWithUrl ? undefined : 'tabs-menu'}>
      {theme.isNewDesign && <Global styles={overflowPopupStyles} />}
      {syncsWithUrl ? (
        <>
          <ScrollIntoViewIfNeeded
            active={location.hash === TAB_ANCHOR_HASH}
            options={{ behavior: 'smooth', block: 'start', scrollMode: 'always' }}
          />
          <MenuContainer>
            <PageMenu {...menuProps} />
            {customActions}
          </MenuContainer>
        </>
      ) : (
        // No MenuContainer on purpose: in menu2 the bar sat directly inside this block container,
        // where its `flex: 1` is inert. A flex wrapper would activate it and move the layout.
        <CompactMenu {...menuProps} />
      )}
      <ContentContainer>{children}</ContentContainer>
    </Container>
  );
};

const Container = styled.div`
  position: relative;
`;

const MenuContainer = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;

  ${legacyOnly(
    theme => css`
      border-bottom: 2px solid ${theme.menuBorder} !important;
      height: 47px;
    `
  )}

  ${newDesignOnly(
    theme => css`
      border-bottom: ${theme.borderWidth.xs} solid var(--border-primary);
      height: 32px;
    `
  )}
`;

const ContentContainer = styled.div`
  padding: 18px 0;
`;

const legacyItem = (theme: Env0Theme) => css`
  color: ${theme.menuItemText} !important;
  border-bottom-color: transparent;
  font-size: 15px;
  font-weight: 600;
  line-height: 31px;
  letter-spacing: -0.08px;
  text-transform: uppercase;
  z-index: 2;

  &-selected:after,
  &:hover:after {
    color: ${theme.primaryBlack} !important;
    border-bottom-width: 3px !important;
    border-bottom-color: ${theme.primaryBlack} !important;
  }
`;

/**
 * https://www.figma.com/design/vhfYlbrrUsQrx6AvWGcE9h/Design-system?node-id=152-21
 *
 * Only what the antd bridge cannot express. Colours for the selected state and the underline live in
 * `menuComponent` in `constants/antd.constants.ts`; what is left is the type ramp, the bar geometry,
 * and the two colours the bridge cannot set without repainting the vertical nav rail that shares this
 * antd component.
 *
 * `ownsBarRule` is which element carries the full-width line under the bar: MenuContainer for `page`,
 * so it runs under customActions too, and the menu itself for `compact`, which has no wrapper.
 */
const newDesignMenu = (theme: Env0Theme, ownsBarRule: boolean) => css`
  background: transparent;
  flex: 1;
  overflow: hidden;
  border-bottom: ${ownsBarRule ? `${theme.borderWidth.xs} solid var(--border-primary)` : 'none'};

  .ant-menu-item {
    /* Figma Nav / Tab: "Label 14 Rubik", which is the label-md rung; label-sm renders 12. */
    ${typeStyle.labelMd}

    /*
     * Sentence case, which is what Nav / Tab specs (APO-465). Only the first letter, because
     * text-transform: capitalize also re-cases labels a call site already set — "Personal settings"
     * became "Personal Settings". Works because antd gives a horizontal item display: inline-block.
     */
    &::first-letter {
      text-transform: uppercase;
    }
    height: 32px;
    line-height: 32px;
    margin: 0;
    padding: 0 ${theme.padding.xs};

    /* antd gives a horizontal item no margin, so the gap is set here — same value as the Tabs wrapper. */
    & + .ant-menu-item {
      margin-left: ${theme.spacing.lg};
    }

    /* Shared with the vertical rail through itemColor / itemHoverColor, so not bridgeable yet. */
    color: var(--text-body);

    &:hover {
      color: var(--text-heading);
    }

    &-disabled {
      color: var(--text-disabled);
    }

    &::after {
      border-bottom-width: ${theme.borderWidth.sm};
    }

    /*
     * antd draws the selected label and its underline from one token, so the bridge sets the lime
     * Figma binds and the label is repainted here. ant-menu-title-content carries only a transition
     * in antd's sheet, so a plain declaration on it wins - no !important, no doubled class.
     */
    &-selected .ant-menu-title-content {
      color: var(--text-heading);
    }
  }

  /*
   * antd insets the indicator 16px per side, a figure sized for its own 20px item padding; against the
   * design system's 8px that left a stub narrower than the label. Doubled class because antd's own
   * selector names three.
   */
  &&.ant-menu-horizontal .ant-menu-item::after {
    inset-inline: 0;
  }
`;

// Two styled components rather than one taking a variant prop: emotion forwards every prop to the
// component it wraps, so a `$variant` would land on antd's Menu and from there on the DOM.
const PageMenu = styled(Menu)`
  ${legacyOnly(
    theme => css`
      background: transparent !important;
      border-bottom-width: 0 !important;
      margin-bottom: -1px;
      flex: 1;
      overflow: hidden;

      .ant-menu-item {
        border-bottom-width: 4px !important;
        ${legacyItem(theme)}
      }
    `
  )}

  ${newDesignOnly(theme => newDesignMenu(theme, false))}
`;

const CompactMenu = styled(Menu)`
  ${legacyOnly(
    theme => css`
      background: transparent !important;
      border-bottom: 2px solid ${theme.menuBorder} !important;
      height: 35px;
      flex: 1;

      .ant-menu-item {
        border-bottom-width: 3px !important;
        padding: 0 22px !important;
        ${legacyItem(theme)}
      }
    `
  )}

  ${newDesignOnly(theme => newDesignMenu(theme, true))}
`;

export default TabsMenu;
